/**
 * Body part: skin, face, arms, legs. Authored in SKIN_AUTHOR indices plus Ink
 * for the eyes only. No contour or shadow here — compose.js owns both.
 */
import { SKIN_AUTHOR } from '../catalog.js';
import {
  canvas,
  fillRect,
  hLine,
  mirrorFrame,
  put,
  vLine,
  walkSheet,
} from '../../pixel-kit.js';
import { FRONT, SHOE_TOP, SIDE, poseFor } from './rig.js';

const INK = 1;
const [SKIN_DK, SKIN_MD, SKIN_LT] = SKIN_AUTHOR;

function blank() {
  return canvas(16, 24);
}

/** Rounded 8-wide chibi head, top row tucked in. */
function head(px, r, bob, { face }) {
  const top = r.headTop + bob;
  fillRect(px, r.headX0 + 1, top, 6, 1, SKIN_MD);
  fillRect(px, r.headX0, top + 1, 8, 5, SKIN_MD);
  fillRect(px, r.headX0 + 1, top + 6, 6, 1, SKIN_MD);
  if (face === 'down') {
    put(px, 5, top + 4, INK); put(px, 6, top + 4, INK);
    put(px, 9, top + 4, INK); put(px, 10, top + 4, INK);
    put(px, 5, top + 5, INK); put(px, 10, top + 5, INK);
  } else if (face === 'left') {
    // profile: single eye near the leading edge, 1px nose tip
    put(px, 5, top + 4, INK); put(px, 6, top + 4, INK);
    put(px, 3, top + 5, SKIN_MD);
  }
}

function frontFrame(gender, direction, frame) {
  const px = blank();
  const r = FRONT;
  const p = poseFor(direction, frame);
  const female = gender === 'female';
  const bob = p.bob;

  // legs stop where the shoes start; dark skin in the gap keeps the
  // contour on the outside of the silhouette
  fillRect(px, r.legLX, r.legTop, 2, SHOE_TOP - r.legTop, SKIN_MD);
  fillRect(px, r.legRX, r.legTop, 2, SHOE_TOP - r.legTop, SKIN_MD);
  fillRect(px, 7, r.legTop, 2, SHOE_TOP - r.legTop, SKIN_DK);

  // torso
  const tx0 = female ? r.torsoX0 : r.torsoX0;
  const tx1 = female ? r.torsoX1 : r.torsoX1;
  fillRect(px, tx0, r.torsoTop + bob, tx1 - tx0 + 1, r.torsoBot - r.torsoTop + 1, SKIN_MD);
  // waist shading
  hLine(px, tx0, tx1, r.torsoBot + bob, SKIN_DK);

  // arms: 2px columns, light hands
  const shoulderY = r.torsoTop + bob;
  const armW = female ? 1 : 2;
  const armLX = female ? r.armL + 1 : r.armL;
  fillRect(px, armLX, shoulderY + p.armYL, armW, 4, SKIN_MD);
  fillRect(px, r.armR, shoulderY + p.armYR, armW, 4, SKIN_MD);
  fillRect(px, armLX, shoulderY + 3 + p.armYL, armW, 2, SKIN_LT);
  fillRect(px, r.armR, shoulderY + 3 + p.armYR, armW, 2, SKIN_LT);

  head(px, r, bob, { face: direction === 'down' ? 'down' : 'none' });
  // neck
  hLine(px, 6, 9, r.headBot + 1 + bob, SKIN_DK);
  return px;
}

function sideFrame(gender, frame) {
  const px = blank();
  const r = SIDE;
  const p = poseFor('left', frame);
  const bob = p.bob;

  // legs: near (mid) strides forward, far (dark) strides back
  const nearX = r.hipX + p.stride;
  const farX = r.hipX - p.stride;
  fillRect(px, farX, r.legTop, 2, SHOE_TOP - r.legTop, SKIN_DK);
  fillRect(px, nearX, r.legTop, 2, SHOE_TOP - r.legTop, SKIN_MD);

  // torso
  const tw = gender === 'female' ? 4 : 5;
  fillRect(px, r.torsoX0, r.torsoTop + bob, tw, r.torsoBot - r.torsoTop + 1, SKIN_MD);
  hLine(px, r.torsoX0, r.torsoX0 + tw - 1, r.torsoBot + bob, SKIN_DK);

  // near arm: shoulder column, hand reaches with the stride
  const shoulderY = r.torsoTop + bob;
  fillRect(px, r.armX, shoulderY, 2, 3, SKIN_MD);
  fillRect(px, r.armX + p.armReach, shoulderY + 3, 2, 2, SKIN_LT);
  if (p.armReach) put(px, r.armX + (p.armReach > 0 ? 1 : 0) + p.armReach / 2, shoulderY + 2, SKIN_MD);

  head(px, r, bob, { face: 'left' });
  hLine(px, 6, 9, r.headBot + 1 + bob, SKIN_DK);
  return px;
}

function drawFrame(gender, direction, frame) {
  if (direction === 'left') return sideFrame(gender, frame);
  if (direction === 'right') return mirrorFrame(sideFrame(gender, frame));
  return frontFrame(gender, direction, frame);
}

export function bodySheet(gender) {
  return walkSheet((direction, frame) => drawFrame(gender, direction, frame));
}
